// Seasonal themes: a banner across the top of the site and animated
// decorations on the home page for holidays and well-known days.
//
// ============================================================================
// SETTINGS
//
// The live settings come from the API, so the admin dashboard can switch the
// theme for every visitor at once:
//
//   GET /api/settings/season          read by the app on load (and every
//                                     10 minutes; see context/SeasonContext.js)
//   PUT /api/admin/settings/season    used by the dashboard to change them,
//                                     with the header X-Admin-Token
//                                     (see backend/settings.go)
//
// The values below are the defaults, used until the API answers or if it
// can't be reached. Each option is the same in the API.
// ============================================================================
export const SEASON_SETTINGS = {
  // Which theme is shown:
  //   "auto"   pick the theme whose dates include today (see SEASONS below)
  //   "off"    no seasonal theme at all
  //   an id    always show that theme, whatever the date. One of:
  //            "newYear", "valentines", "easter", "freedomDay", "mothersDay",
  //            "youthDay", "fathersDay", "womensDay", "heritageDay",
  //            "halloween", "blackFriday", "christmas"
  mode: "auto",

  // Show the banner across the top of every page (not on invitations
  // themselves, which keep their own design).
  banner: true,

  // Show the animated decorations (snow, hearts, fireworks…) in the banner.
  effects: true,

  // Let visitors close the banner. Closing hides that theme's banner for
  // them until the next theme starts.
  dismissible: true,

  // Keep the banner on screen while scrolling: it sticks to the top with
  // the header and slims down to a compact strip once the page scrolls.
  stickyBanner: false,

  // Let the decorations drift gently over every page (behind the header and
  // dialogs, never blocking clicks), so the theme stays in view while
  // scrolling. When false, they only play in the home page hero.
  ambient: true,

  // How many decorations drift over the page, from 0.2 (a few) to 1 (lots).
  ambientDensity: 0.45,

  // Dress the header too: a thin animated line in the theme's colours and
  // the theme's emoji beside the logo.
  headerAccent: true,

  // Themes to never show in "auto" mode, e.g. ["halloween", "blackFriday"].
  disabled: [],
};

// ============================================================================
// THE THEMES
//
// Each theme has:
//   id        used in the settings' "mode" and "disabled"; its banner wording is
//             in the translation files under seasons.<id>
//   when      (year) => [start, end] — the dates it is active ("auto" mode),
//             both inclusive, as JavaScript Dates
//   emoji     shown in the banner
//   colors    banner gradient [from, to] and text colour
//   effect    the animation: "snow", "fireworks", "hearts", "eggs", "flags",
//             "flowers", "stars", "leaves", "bats" or "tags"
//   link      where the banner's button goes (an existing page)
//
// When two themes overlap, the first one in this list wins.
// ============================================================================

const day = (year, month, date) => new Date(year, month - 1, date);
const addDays = (date, days) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);

// The nth given weekday of a month (weekday: 0 = Sunday … 6 = Saturday).
const nthWeekday = (year, month, weekday, n) => {
  const first = day(year, month, 1);
  const offset = (weekday - first.getDay() + 7) % 7;
  return day(year, month, 1 + offset + (n - 1) * 7);
};

// Easter Sunday (Western churches), by the anonymous Gregorian algorithm.
export const easterSunday = (year) => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const date = ((h + l - 7 * m + 114) % 31) + 1;
  return day(year, month, date);
};

export const SEASONS = [
  {
    // Black Friday week, from the Monday before to Cyber Monday.
    id: "blackFriday",
    when: (year) => {
      const blackFriday = addDays(nthWeekday(year, 11, 4, 4), 1); // day after US Thanksgiving
      return [addDays(blackFriday, -4), addDays(blackFriday, 3)];
    },
    emoji: "🏷️",
    colors: { from: "#0b0b0f", to: "#2a2a35", text: "#ffd166" },
    effect: "tags",
    link: "/templates",
  },
  {
    id: "christmas",
    when: (year) => [day(year, 12, 1), day(year, 12, 26)],
    emoji: "🎄",
    colors: { from: "#9b1c2c", to: "#1f6f43", text: "#ffffff" },
    effect: "snow",
    link: "/templates?type=dinner",
  },
  {
    // Spans the turn of the year: late December and early January.
    id: "newYear",
    when: (year) => [day(year, 12, 27), day(year + 1, 1, 5)],
    emoji: "🎆",
    colors: { from: "#0d1b5e", to: "#3b0f6b", text: "#ffd166" },
    effect: "fireworks",
    link: "/templates?type=other",
  },
  {
    id: "valentines",
    when: (year) => [day(year, 2, 1), day(year, 2, 14)],
    emoji: "💝",
    colors: { from: "#e91e7a", to: "#ff5a5f", text: "#ffffff" },
    effect: "hearts",
    link: "/templates?type=dinner",
  },
  {
    // The week before Easter until Easter Monday.
    id: "easter",
    when: (year) => {
      const sunday = easterSunday(year);
      return [addDays(sunday, -9), addDays(sunday, 1)];
    },
    emoji: "🐣",
    colors: { from: "#b9e6d3", to: "#f7c6e0", text: "#3c2a66" },
    effect: "eggs",
    link: "/templates?type=other",
  },
  {
    // Freedom Day, 27 April (South Africa).
    id: "freedomDay",
    when: (year) => [day(year, 4, 20), day(year, 4, 27)],
    emoji: "🇿🇦",
    colors: { from: "#007a4d", to: "#002395", text: "#ffffff" },
    effect: "flags",
    link: "/templates?type=other",
  },
  {
    // Second Sunday of May.
    id: "mothersDay",
    when: (year) => {
      const sunday = nthWeekday(year, 5, 0, 2);
      return [addDays(sunday, -7), sunday];
    },
    emoji: "💐",
    colors: { from: "#f7a8c0", to: "#c9566b", text: "#ffffff" },
    effect: "flowers",
    link: "/templates?type=dinner",
  },
  {
    // Youth Day, 16 June (South Africa).
    id: "youthDay",
    when: (year) => [day(year, 6, 10), day(year, 6, 16)],
    emoji: "✊",
    colors: { from: "#ff9500", to: "#e4572e", text: "#ffffff" },
    effect: "flags",
    link: "/templates?type=birthday",
  },
  {
    // Third Sunday of June.
    id: "fathersDay",
    when: (year) => {
      const sunday = nthWeekday(year, 6, 0, 3);
      return [addDays(sunday, -7), sunday];
    },
    emoji: "👔",
    colors: { from: "#14213d", to: "#2563eb", text: "#ffffff" },
    effect: "stars",
    link: "/templates?type=dinner",
  },
  {
    // National Women's Day, 9 August (South Africa).
    id: "womensDay",
    when: (year) => [day(year, 8, 2), day(year, 8, 9)],
    emoji: "🌸",
    colors: { from: "#7b2ff7", to: "#e91e7a", text: "#ffffff" },
    effect: "flowers",
    link: "/templates?type=dinner",
  },
  {
    // Heritage Day ("Braai Day"), 24 September (South Africa).
    id: "heritageDay",
    when: (year) => [day(year, 9, 17), day(year, 9, 24)],
    emoji: "🔥",
    colors: { from: "#c2623f", to: "#f2b134", text: "#ffffff" },
    effect: "leaves",
    link: "/templates?type=dinner",
  },
  {
    id: "halloween",
    when: (year) => [day(year, 10, 24), day(year, 10, 31)],
    emoji: "🎃",
    colors: { from: "#1a1016", to: "#ff7518", text: "#ffffff" },
    effect: "bats",
    link: "/templates?type=birthday",
  },
];

export const getSeason = (id) => SEASONS.find((season) => season.id === id) || null;

// The theme active on `date` under the given settings.
export const seasonFor = (date = new Date(), settings = SEASON_SETTINGS) => {
  if (settings.mode === "off") return null;
  if (settings.mode !== "auto") return getSeason(settings.mode);
  const today = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  // Check last year's ranges too, for themes that run into January.
  for (const season of SEASONS) {
    if ((settings.disabled || []).includes(season.id)) continue;
    for (const year of [today.getFullYear() - 1, today.getFullYear()]) {
      const [start, end] = season.when(year);
      if (today >= start && today <= end) return season;
    }
  }
  return null;
};
